/* SinceYouLastLookedDigest — editorial 3-5 plain-English headlines that
   Jacinta hand-picked from what changed since your last visit. NOT a log.
   Each entry has an icon, a one-line headline, optional CTA. */

import { useNavigate } from 'react-router-dom';
import { ArrowRight, AlertCircle, CheckCircle2, Sparkles, UserCheck, MessageSquare } from 'lucide-react';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';

export interface SinceYouLastLookedDigestProps {
    jobs: JobPosting[];
    tasks: TaskItem[];
    events: SyncedCalendarEvent[];
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
    accent?: string;
    accentBg?: string;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

type DigestKind = 'flag' | 'win' | 'anomaly' | 'arrival' | 'message';

interface DigestEntry {
    id: string;
    kind: DigestKind;
    headline: React.ReactNode;
    cta?: { label: string; href: string };
}

const KIND_META: Record<DigestKind, { Icon: typeof Sparkles; tint: string; bg: string }> = {
    flag: { Icon: AlertCircle, tint: 'rgb(192 88 11)', bg: 'rgba(239, 154, 91, 0.12)' },
    win: { Icon: CheckCircle2, tint: 'rgb(20 143 63)', bg: 'rgba(86, 218, 139, 0.14)' },
    anomaly: { Icon: AlertCircle, tint: 'rgb(242 0 0)', bg: 'rgba(255, 80, 80, 0.1)' },
    arrival: { Icon: Sparkles, tint: 'rgb(108 70 145)', bg: 'rgba(167, 124, 198, 0.16)' },
    message: { Icon: MessageSquare, tint: 'rgb(0 72 205)', bg: 'rgba(0, 132, 255, 0.12)' },
};

function buildDigest(jobs: JobPosting[], tasks: TaskItem[], events: SyncedCalendarEvent[]): DigestEntry[] {
    const primary = jobs[0];
    const secondary = jobs[1];
    const high = tasks.filter((t) => t.priority === 'high');
    const next = events[0];

    const entries: DigestEntry[] = [];

    if (high.length > 0) {
        const firstHigh = high[0];
        entries.push({
            id: 'flag',
            kind: 'flag',
            headline: (
                <>
                    <strong>{high.length} candidates flagged</strong> for your judgment — borderline
                    calls on{' '}
                    {primary ? <strong>{primary.title}</strong> : 'open roles'}, where I want your taste
                    before moving them.
                </>
            ),
            cta: { label: `Review ${firstHigh.candidateName}`, href: `/v2/jobs` },
        });
    }

    if (primary) {
        const seed = hash(primary._id);
        const passed = 5 + (seed % 4);
        entries.push({
            id: 'win',
            kind: 'win',
            headline: (
                <>
                    <strong>{passed} new candidates</strong> passed my bar for{' '}
                    <strong>{primary.title}</strong> overnight — strongest pool this week.
                </>
            ),
            cta: { label: 'Open pipeline', href: `/v2/jobs/${primary._id}/plan` },
        });
    }

    if (secondary) {
        entries.push({
            id: 'arrival',
            kind: 'arrival',
            headline: (
                <>
                    Pre-drafted outreach for{' '}
                    <strong>{8 + (hash(secondary._id) % 9)} warm referrals</strong> on{' '}
                    <strong>{secondary.title}</strong> — ready for your approval.
                </>
            ),
            cta: { label: 'Review drafts', href: `/v2/jobs/${secondary._id}/plan` },
        });
    }

    if (next) {
        const who = next.attendees?.[0]?.name?.split(' ')[0] || 'A candidate';
        entries.push({
            id: 'message',
            kind: 'message',
            headline: (
                <>
                    <strong>{who}</strong> confirmed{' '}
                    {next.title.toLowerCase().includes('interview') ? '' : 'the '}
                    {next.title} for{' '}
                    {new Date(next.startDateTime).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                    })}
                    .
                </>
            ),
            cta: { label: 'Open calendar', href: '/v2/calendar' },
        });
    }

    if (entries.length < 3 && primary) {
        entries.push({
            id: 'anomaly',
            kind: 'anomaly',
            headline: (
                <>
                    Sourcing for <strong>{primary.title}</strong> hit a LinkedIn rate limit — paused,
                    will retry in 1h.
                </>
            ),
            cta: { label: 'See diagnostics', href: `/v2/jobs/${primary._id}/plan` },
        });
    }

    return entries.slice(0, 5);
}

export function SinceYouLastLookedDigest({
    jobs,
    tasks,
    events,
    surface = 'rgba(255,255,255,0.7)',
    text = '#2a241e',
    muted = '#6f6660',
    rule = 'rgba(120, 90, 50, 0.08)',
    accent = '#fd775e',
    accentBg = 'rgba(253, 119, 94, 0.06)',
}: SinceYouLastLookedDigestProps) {
    const navigate = useNavigate();
    const entries = buildDigest(jobs, tasks, events);

    return (
        <div
            style={{
                background: surface,
                borderRadius: 18,
                padding: 20,
                border: `1px solid ${rule}`,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
                <span
                    style={{
                        fontFamily: 'var(--v2-font-serif)',
                        fontSize: 18,
                        fontWeight: 500,
                        color: text,
                        letterSpacing: '-0.01em',
                    }}
                >
                    Since you last looked
                </span>
                <span
                    style={{
                        fontSize: 10,
                        color: muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        fontWeight: 600,
                    }}
                >
                    edited by Jacinta
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {entries.map((e) => {
                    const meta = KIND_META[e.kind];
                    const Icon = meta.Icon;
                    return (
                        <div
                            key={e.id}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '28px minmax(0, 1fr)',
                                gap: 12,
                                alignItems: 'flex-start',
                                paddingBottom: 12,
                                borderBottom: `1px dashed ${rule}`,
                            }}
                        >
                            <div
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 8,
                                    background: meta.bg,
                                    color: meta.tint,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    marginTop: 1,
                                }}
                            >
                                <Icon size={14} strokeWidth={2} />
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: 13.5,
                                        lineHeight: 1.5,
                                        color: text,
                                    }}
                                >
                                    {e.headline}
                                </div>
                                {e.cta && (
                                    <button
                                        type="button"
                                        onClick={() => navigate(e.cta!.href)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: accent,
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            padding: 0,
                                            marginTop: 4,
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            fontFamily: 'inherit',
                                        }}
                                    >
                                        {e.cta.label}
                                        <ArrowRight size={11} strokeWidth={2.25} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
